<template>
  <div class="WitnessSelectionCard pa-4">
    <div class="d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <div class="WitnessSelectionCard-color mr-2"
             :style="{backgroundColor: `rgb(var(--v-theme-surface-${color}))`}"
        ></div>
        <span class="text--small text--bold">{{ t('selectSigner', { signerNumber: displayedIndex }) }}</span>
      </div>
      <app-button v-if="canDelete"
                 type="icon"
                 @click="$emit('delete')"
      >
        <template #left-icon>
          <app-material-icon name="delete" />
        </template>
      </app-button>
    </div>
    <div class="mt-2 mb-4">
      <v-autocomplete :model-value="witnessSelection.signer"
                      :items="computedSigners"
                      :error-messages="signerErrors"
                      :hide-details="!signerErrors.length"
                      auto-select-first
                      color="brand-secondary"
                      clearable
                      :readonly="witnessSelection.signer !== null"
                      clear-icon="close"
                      item-value="id"
                      item-title="fullName"
                      menu-icon="arrow_drop_down"
                      variant="outlined"
                      density="compact"
                      :class="{'autocomplete_witness--clickable' : witnessSelection.signer === null}"
                      :placeholder="t('selectSignerPlaceholder')"
                      @update:model-value="onSignerInput"
      >
        <template #item="{ item, props }">
          <v-list-item v-bind="props">
            <template #prepend>
              <app-avatar class="mr-2" :initials="item.raw.initials" />
            </template>
            <template #title>
              <span class="text--small text-grey-700">
                {{ item.raw.fullName }}
              </span>
            </template>
          </v-list-item>
        </template>
      </v-autocomplete>
    </div>
    <template v-if="witnessSelection.signer">
      <div class="d-flex align-center">
        <app-material-icon
          name="link"
          :style="{color: `rgb(var(--v-theme-icon-${color}))`}"
        />
        <span class="text--small text--bold">
          {{ t('selectWitnesses') }}
        </span>
      </div>
      <div class="mt-3">
        <v-autocomplete :model-value="witnessSelection.witness"
                        :items="computedWitnesses"
                        :menu-props="{closeOnContentClick: witnessSelection.witness.length >=2}"
                        :error-messages="witnessesErrors"
                        :hide-details="!witnessesErrors.length"
                        auto-select-first
                        color="brand-secondary"
                        clearable
                        :readonly="hasTwoWitnesses || !canAddRecipient"
                        item-title="fullName"
                        item-value="id"
                        hide-selected
                        clear-icon="close"
                        multiple
                        menu-icon="arrow_drop_down"
                        variant="outlined"
                        density="compact"
                        class="autocomplete_witness"
                        :class="{'autocomplete_witness--clickable' : !hasTwoWitnesses}"
                        :placeholder="t('selectWitnessesPlaceholder')"
                        @update:model-value="onWitnessInput"
        >
          <template #selection="{item}">
            <v-chip
              closable
              density="compact"
              color="grey-900"
              label
              @click:close="onItemDeleteClick(item.raw)"
            >
              <span class="mr-2">{{ item.raw.fullName }}</span>
            </v-chip>
          </template>
          <template #item="{item, props}">
            <v-list-item v-bind="props" :class="{'d-none': hasTwoWitnesses}">
              <template #prepend>
                <app-avatar class="mr-2" :initials="item.raw.initials" />
              </template>
              <template #title>
                <span class="text--small text-grey-700">
                  {{ item.raw.fullName }}
                </span>
              </template>
              <template #append>
                <v-chip
                  v-if="item.raw.isSigner"
                  size="small"
                  color="indigo"
                >
                  <span class="text--x-small text--bold">
                    {{ $t('common.signer') }}
                  </span>
                </v-chip>
              </template>
            </v-list-item>
          </template>
        </v-autocomplete>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { defineComponent, PropType } from 'vue'
import { TranslateResult } from 'vue-i18n'

import { formFieldErrors } from '@/common/utils/form'
import { useTranslation } from '@/common/utils/translation'
import { SignersColors } from '@/project/signing-checklist/prepare-signature/signers-colors'

import { SignerWitnesses } from './types';

type MemberAutocomplete = Member & {
  disabled: boolean;
  isSigner: boolean;
};

export default defineComponent({
  name: 'WitnessSelectionCard',
  setup () {
    const { t } = useTranslation({ keyPrefix: 'project.signing-checklist.witness.WitnessSelectionCard' })

    return {
      t,
      v$: useVuelidate(),
    }
  },
  props: {
    witnessSelection: {
      type: Object as PropType<SignerWitnesses>,
      required: true,
    },
    index: {
      type: Number as PropType<number>,
      required: true,
    },
    signers: {
      type: Array as PropType<Member[]>,
      required: true,
    },
    witnesses: {
      type: Array as PropType<Member[]>,
      required: true,
    },
    selectedSigners: {
      type: Array as PropType<Array<number>>,
      required: true,
    },
    canDelete: {
      type: Boolean,
      required: true,
    },
    canAddRecipient: {
      type: Boolean,
      required: true,
    },
  },
  validations: {
    witnessSelection: {
      signer: { required, $autoDirty: true },
      witness: { required, $autoDirty: true },
    },
  },
  computed: {
    color (): string {
      const i = this.index % (Object.entries(SignersColors).length / 2)
      return SignersColors[i]
    },
    displayedIndex (): number {
      return this.index + 1
    },
    hasTwoWitnesses (): boolean {
      return this.witnessSelection.witness.length === 2
    },
    computedSigners (): Member[] {
      return this.signers.filter(s =>
        !this.selectedSigners.includes(s.id) ||
        (this.selectedSigners.includes(s.id) && s.id === this.witnessSelection.signer),
      )
    },
    computedWitnesses () {
      const witnessesAutocompleteList: MemberAutocomplete[] = []
      this.witnesses.map(s => {
        witnessesAutocompleteList.push({ ...s, disabled: this.witnessSelection.witness.includes(s.id), isSigner: this.signers.includes(s) })
      })
      return witnessesAutocompleteList.sort((a, b) => Number(a.isSigner) - Number(b.isSigner))
    },
    signerErrors (): TranslateResult[] {
      if (this.witnessSelection.witness.length === 0 && this.selectedSigners.length === 0) {
        return []
      }
      return formFieldErrors(
        this.v$['witnessSelection'].signer,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.t('signatory') })],
        ],
      )
    },
    witnessesErrors (): TranslateResult[] {
      return formFieldErrors(
        this.v$['witnessSelection'].witness,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.t('witnesses') })],
        ],
      )
    },
  },
  methods: {
    onSignerInput (signerId: number | undefined) {
      if (!signerId) {
        this.$emit('add-witness', [])
      }
      this.$emit('add-signer', signerId)
    },
    onWitnessInput (witnessesIds: Array<number>) {
      if (witnessesIds.length <= 2) {
        this.$emit('add-witness', witnessesIds)
      }
    },
    onItemDeleteClick (witness: ObjectWith<{ id: number }>): void {
      const newArray = this.witnessSelection.witness.filter(
        (witnessId: number) => witnessId !== witness.id,
      )
      this.$emit('add-witness', newArray)
    },
  },
})
</script>

<style scoped lang="scss">
.WitnessSelectionCard {
  border-radius: 8px;
  border: 1px solid rgb(var(--v-theme-border-primary));
  margin-bottom: 8px;

  &-color {
    width: 12px;
    height: 12px;
    border-radius: 4px;
  }
}
</style>
